import { useEffect, useRef, useState } from "react";
import api, { formatApiError, mediaUrl } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { CognitiveScan } from "@/components/CognitiveScan";
import { StatusBadge } from "@/components/StatusBadge";
import { ScoreDial } from "@/components/ScoreDial";
import { AppIcon } from "@/components/AppIcon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  UploadCloud, Trash2, Rocket, Wrench, ShieldCheck, ShieldAlert,
  Package, FileCheck2, Boxes, AlertTriangle, Image as ImageIcon,
} from "lucide-react";

function bytes(n) {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / Math.pow(1024, i)).toFixed(1)} ${u[i]}`;
}

function Stat({ label, value, tone = "text-foreground" }) {
  return (
    <div className="border border-border bg-[#0C0D14] p-4">
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{label}</div>
      <div className={`font-mono font-bold text-2xl ${tone}`}>{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useLang();
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [name, setName] = useState("");
  const [version, setVersion] = useState("1.0.0");
  const [file, setFile] = useState(null);
  const [phase, setPhase] = useState("idle");
  const [result, setResult] = useState(null);
  const [publishApp, setPublishApp] = useState(null);
  const [pubForm, setPubForm] = useState({ price: 0, category: "General", description: "" });
  const [iconFile, setIconFile] = useState(null);
  const [shotFiles, setShotFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();
  const iconRef = useRef();
  const shotRef = useRef();

  const load = async () => {
    const [a, s] = await Promise.all([api.get("/apps/mine"), api.get("/stats")]);
    setApps(a.data);
    setStats(s.data);
  };
  useEffect(() => { load(); }, []);

  const doUpload = async () => {
    if (!file || !name) { toast.error(t("toast.nameApkRequired")); return; }
    setPhase("uploading");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", name);
      fd.append("version", version);
      const { data } = await api.post("/apps/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      setPhase("scanning");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("toast.uploadFailed"));
      setPhase("idle");
    }
  };

  const resetUpload = () => {
    setUploadOpen(false);
    setPhase("idle"); setResult(null); setName(""); setVersion("1.0.0"); setFile(null);
    load();
  };

  const del = async (id) => {
    await api.delete(`/apps/${id}`);
    toast.success(t("toast.appDeleted"));
    load();
  };

  const requestReg = async (id) => {
    await api.post(`/apps/${id}/request-regularization`);
    toast.info(t("toast.regDone"));
    load();
  };

  const openPublish = (app) => {
    setPublishApp(app);
    setPubForm({ price: app.price || 0, category: app.category || "General", description: app.description || "" });
    setIconFile(null);
    setShotFiles([]);
  };

  const confirmPublish = async () => {
    setSaving(true);
    try {
      await api.post(`/apps/${publishApp.id}/publish`, pubForm);
      if (iconFile || shotFiles.length) {
        const fd = new FormData();
        if (iconFile) fd.append("icon", iconFile);
        shotFiles.forEach((f) => fd.append("screenshots", f));
        await api.post(`/apps/${publishApp.id}/media`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(t("toast.published", { name: publishApp.name }));
      setPublishApp(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("toast.failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="font-mono text-xs text-primary tracking-[0.3em] mb-2">{t("dashboard.tag")}</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl">{t("dashboard.title")}</h1>
        </div>
        <Button data-testid="open-upload-btn" onClick={() => setUploadOpen(true)}
          className="rounded-none bg-primary text-primary-foreground font-mono uppercase tracking-wider hover:bg-primary/90 active:scale-95 transition-transform">
          <UploadCloud className="w-4 h-4 mr-2" /> {t("dashboard.uploadApp")}
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          <Stat label={t("dashboard.totalApps")} value={stats.total_apps} />
          <Stat label={t("dashboard.verified")} value={stats.verified} tone="text-[#00FF66]" />
          <Stat label={t("dashboard.notVerified")} value={stats.not_verified} tone="text-[#FF2A2A]" />
          <Stat label={t("dashboard.published")} value={stats.published} tone="text-primary" />
          <Stat label={t("dashboard.avgRisk")} value={stats.avg_risk} tone="text-[#FFD700]" />
        </div>
      )}

      {apps.length === 0 ? (
        <div className="border border-dashed border-border p-16 text-center grid-texture" data-testid="empty-apps">
          <Boxes className="w-10 h-10 text-primary/50 mx-auto mb-4" />
          <p className="text-muted-foreground font-mono text-sm">{t("dashboard.empty")}</p>
        </div>
      ) : (
        <div className="grid gap-5">
          {apps.map((app, idx) => (
            <motion.div key={app.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}
              data-testid={`app-card-${app.id}`} className="border border-border bg-[#0C0D14] p-6 hover:border-primary/50 transition-colors">
              <div className="flex flex-col lg:flex-row gap-6">
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <AppIcon name={app.name} category={app.category} src={mediaUrl(app.icon_id)} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-display font-bold text-lg truncate">{app.name}</h3>
                      <StatusBadge status={app.status} governance={app.regularization_requested ? "REGULARIZABLE" : app.governance_status} />
                    </div>
                    <div className="font-mono text-xs text-muted-foreground mt-1">
                      v{app.version} · {bytes(app.size_bytes)} · {app.governance_status}
                    </div>
                    <div className="mt-4">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">{t("dashboard.permissionMap")}</div>
                      <div className="flex flex-wrap gap-1.5">
                        {app.permissions.map((p) => {
                          const danger = app.dangerous_permissions?.includes(p);
                          return (
                            <span key={p} className={`font-mono text-[10px] px-2 py-0.5 border ${danger ? "border-[#FF2A2A]/50 text-[#FF2A2A] bg-[#FF2A2A]/5" : "border-border text-muted-foreground"}`}>
                              {danger && <AlertTriangle className="w-2.5 h-2.5 inline mr-1" />}{p}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <ScoreDial value={app.risk_score} label={t("dashboard.risk")} tone="risk" testId={`risk-${app.id}`} />
                  <ScoreDial value={app.coherence_score} label={t("dashboard.coherence")} testId={`coherence-${app.id}`} />
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-5 pt-5 border-t border-border">
                {!app.published && (
                  <Button data-testid={`publish-btn-${app.id}`} onClick={() => openPublish(app)}
                    className="rounded-none bg-primary text-primary-foreground font-mono text-xs uppercase tracking-wider hover:bg-primary/90">
                    <Rocket className="w-3.5 h-3.5 mr-1.5" /> {t("dashboard.publish")}
                  </Button>
                )}
                {app.published && (
                  <span className="flex items-center gap-1.5 font-mono text-xs text-primary px-3">
                    <FileCheck2 className="w-3.5 h-3.5" /> {t("dashboard.liveOnMarket")} · {app.price > 0 ? `€${app.price}` : t("common.free")}
                  </span>
                )}
                {app.status === "NOT_VERIFIED" && (
                  <Button data-testid={`regularize-btn-${app.id}`} variant="outline" disabled={app.regularization_requested}
                    onClick={() => requestReg(app.id)}
                    className="rounded-none border-[#FFD700]/50 text-[#FFD700] font-mono text-xs uppercase tracking-wider hover:bg-[#FFD700]/10 disabled:opacity-50">
                    <Wrench className="w-3.5 h-3.5 mr-1.5" /> {app.regularization_requested ? t("dashboard.regRequested") : t("dashboard.requestReg")}
                  </Button>
                )}
                <Button data-testid={`delete-btn-${app.id}`} variant="outline" onClick={() => del(app.id)}
                  className="rounded-none border-border text-muted-foreground font-mono text-xs uppercase tracking-wider hover:border-[#FF2A2A]/50 hover:text-[#FF2A2A] ml-auto">
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> {t("dashboard.delete")}
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Upload / Verification dialog */}
      <Dialog open={uploadOpen} onOpenChange={(o) => { if (!o) resetUpload(); }}>
        <DialogContent className="rounded-none bg-[#0C0D14] border-primary/40 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {phase === "result" ? t("dashboard.resultTitle") : t("dashboard.appUpload")}
            </DialogTitle>
          </DialogHeader>

          {phase === "idle" && (
            <div className="space-y-4">
              <p className="text-muted-foreground text-sm">{t("dashboard.uploadHint")}</p>
              <div>
                <Label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{t("dashboard.appName")}</Label>
                <Input data-testid="upload-name" value={name} onChange={(e) => setName(e.target.value)}
                  className="rounded-none bg-[#05050A] border-border focus-visible:ring-primary mt-1.5" placeholder="Rexx SecureFlow" />
              </div>
              <div>
                <Label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{t("common.version")}</Label>
                <Input data-testid="upload-version" value={version} onChange={(e) => setVersion(e.target.value)}
                  className="rounded-none bg-[#05050A] border-border focus-visible:ring-primary mt-1.5" />
              </div>
              <div onClick={() => fileRef.current?.click()} data-testid="file-dropzone"
                className="border border-dashed border-primary/40 p-8 text-center cursor-pointer hover:border-primary transition-colors grid-texture">
                <UploadCloud className="w-8 h-8 text-primary mx-auto mb-2" />
                <div className="font-mono text-xs text-muted-foreground">{file ? file.name : t("dashboard.selectApk")}</div>
                <input ref={fileRef} type="file" accept=".apk,application/vnd.android.package-archive,*/*" className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="file-input" />
              </div>
              <Button data-testid="start-verification-btn" onClick={doUpload}
                className="w-full rounded-none bg-primary text-primary-foreground font-mono uppercase tracking-wider hover:bg-primary/90">
                {t("dashboard.startVerification")}
              </Button>
            </div>
          )}

          {phase === "uploading" && (
            <div className="py-10 text-center font-mono text-sm text-primary animate-pulse">{t("dashboard.uploading")}</div>
          )}

          {phase === "scanning" && result && <CognitiveScan result={result} onComplete={() => setPhase("result")} />}

          {phase === "result" && result && (
            <div className="space-y-5" data-testid="verification-result">
              <div className={`border p-6 text-center ${result.status === "VERIFIED" ? "border-[#00FF66]/50 bg-[#00FF66]/5" : "border-[#FF2A2A]/50 bg-[#FF2A2A]/5"}`}>
                {result.status === "VERIFIED"
                  ? <ShieldCheck className="w-12 h-12 text-[#00FF66] mx-auto mb-3" />
                  : <ShieldAlert className="w-12 h-12 text-[#FF2A2A] mx-auto mb-3" />}
                <div className={`font-display font-bold text-xl ${result.status === "VERIFIED" ? "text-[#00FF66]" : "text-[#FF2A2A]"}`}>
                  {result.status === "VERIFIED" ? t("dashboard.verified").toUpperCase() : t("dashboard.notVerified").toUpperCase()}
                </div>
                <p className="text-muted-foreground text-sm mt-2">
                  {result.status === "VERIFIED" ? t("dashboard.verifiedMsg") : t("dashboard.notVerifiedMsg")}
                </p>
              </div>
              <div className="flex justify-center gap-8">
                <ScoreDial value={result.risk_score} label={t("dashboard.risk")} tone="risk" />
                <ScoreDial value={result.coherence_score} label={t("dashboard.coherence")} />
                <ScoreDial value={result.reputation_score} label={t("dashboard.reputation")} />
              </div>
              <DialogFooter className="gap-2 sm:gap-2">
                {result.status === "VERIFIED" ? (
                  <Button data-testid="result-close-verified" onClick={resetUpload}
                    className="w-full rounded-none bg-primary text-primary-foreground font-mono uppercase tracking-wider">
                    <Package className="w-4 h-4 mr-2" /> {t("dashboard.doneManage")}
                  </Button>
                ) : (
                  <Button data-testid="result-close-notverified" onClick={resetUpload} variant="outline"
                    className="w-full rounded-none border-[#FFD700]/50 text-[#FFD700] font-mono uppercase tracking-wider hover:bg-[#FFD700]/10">
                    <Wrench className="w-4 h-4 mr-2" /> {t("dashboard.closeReg")}
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Publish dialog */}
      <Dialog open={!!publishApp} onOpenChange={(o) => { if (!o) setPublishApp(null); }}>
        <DialogContent className="rounded-none bg-[#0C0D14] border-primary/40 max-h-[90vh] overflow-y-auto" data-testid="publish-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("dashboard.publishTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{t("dashboard.priceEuro")}</Label>
              <Input data-testid="publish-price" type="number" min="0" value={pubForm.price}
                onChange={(e) => setPubForm({ ...pubForm, price: Number(e.target.value) })}
                className="rounded-none bg-[#05050A] border-border focus-visible:ring-primary mt-1.5" />
            </div>
            <div>
              <Label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{t("common.category")}</Label>
              <div className="grid grid-cols-3 gap-2 mt-1.5">
                {["Security", "Finance", "Productivity", "Gaming", "Social", "Tools"].map((c) => (
                  <button key={c} type="button" onClick={() => setPubForm({ ...pubForm, category: c })}
                    className={`py-2 font-mono text-[10px] uppercase tracking-wider border transition-colors ${
                      pubForm.category === c ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:border-primary/50"
                    }`}>{c}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{t("dashboard.detailedDesc")}</Label>
              <Textarea data-testid="publish-description" value={pubForm.description}
                onChange={(e) => setPubForm({ ...pubForm, description: e.target.value })}
                className="rounded-none bg-[#05050A] border-border focus-visible:ring-primary mt-1.5" rows={4} />
            </div>

            {/* Icon */}
            <div>
              <Label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{t("dashboard.appIcon")}</Label>
              <div className="flex items-center gap-3 mt-1.5">
                {iconFile ? (
                  <img src={URL.createObjectURL(iconFile)} alt="" className="w-14 h-14 object-cover border border-primary/40" />
                ) : (
                  <AppIcon name={publishApp?.name} category={pubForm.category} src={mediaUrl(publishApp?.icon_id)} size={56} />
                )}
                <Button type="button" variant="outline" data-testid="pick-icon-btn" onClick={() => iconRef.current?.click()}
                  className="rounded-none border-border font-mono text-xs uppercase hover:border-primary/50 hover:text-primary">
                  <ImageIcon className="w-3.5 h-3.5 mr-1.5" /> {t("dashboard.selectIcon")}
                </Button>
                <input ref={iconRef} type="file" accept="image/*" className="hidden" data-testid="icon-input"
                  onChange={(e) => setIconFile(e.target.files?.[0] || null)} />
              </div>
            </div>

            {/* Screenshots */}
            <div>
              <Label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{t("dashboard.screenshots")}</Label>
              <div onClick={() => shotRef.current?.click()} data-testid="screenshots-dropzone"
                className="border border-dashed border-border p-4 mt-1.5 text-center cursor-pointer hover:border-primary/50 transition-colors">
                <div className="font-mono text-xs text-muted-foreground">
                  {shotFiles.length ? `${shotFiles.length} ${t("market.screenshotsLabel")}` : t("dashboard.selectShots")}
                </div>
                <input ref={shotRef} type="file" accept="image/*" multiple className="hidden" data-testid="screenshots-input"
                  onChange={(e) => setShotFiles(Array.from(e.target.files || []).slice(0, 8))} />
              </div>
              {shotFiles.length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {shotFiles.map((f, i) => (
                    <img key={i} src={URL.createObjectURL(f)} alt="" className="w-16 h-28 object-cover border border-border" />
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="confirm-publish-btn" onClick={confirmPublish} disabled={saving}
              className="rounded-none bg-primary text-primary-foreground font-mono uppercase tracking-wider">
              <Rocket className="w-4 h-4 mr-2" /> {saving ? t("auth.processing") : t("dashboard.publishNow")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
